import { VeraPromptGuideOptions, searchStatusLine } from './veraPromptGuideShared';

export function buildGlobalSystemGuide(options: VeraPromptGuideOptions): string {
  const searchOn = !!options.googleSearchEnabled;
  return `# Vera Investor Chat — System Prompt

## What you are editing
The **System Prompt** is Vera's main coaching and behaviour instruction for **investor-facing global chat** (web floating chat + native app Vera tab). It is **not** shown to admins during Firestore chat assist.

**Coded production file:** \`investment-management-backend/src/services/vera-system-instruction.md\`

## When it runs
- Every turn of \`POST /vera/global-chat\` for a logged-in investor contact.
- Also used for fund-opportunity scoped Vera chat (\`POST /fundOpportunities/:id/chat\`) when no separate override exists — same instruction family.

## How the backend assembles the model call
1. **Intent classifier** runs **in parallel** (separate fast model + Intent Prompt). Result: \`ADVICE\`, \`LOCATION\`, or \`ALLOWED\`.
2. If \`ADVICE\`: the request is blocked before the main Vera call (financial advice guardrail).
3. Portfolio context markdown is loaded from the contact's AI context cache (investments, EOIs, movements, etc.).
4. FAQ markdown and **today's date** (from client or server) are injected into the cached context block.
5. **This System Prompt** becomes Gemini \`systemInstruction\` (sandbox preview can override via GCS for testing only).
6. Chat history + the investor's new message are sent as conversation turns.
7. If intent is \`LOCATION\`, verified Google Maps context may be appended to the user message.
${searchOn ? `8. **Google Search is enabled** — Vera uses **inline** context delivery (Vertex prompt cache is skipped). The **Google Search Grounding Prompt** is appended to this system instruction and the \`googleSearch\` tool is attached.` : `8. **Google Search is off** — Vera prefers a Vertex prompt cache when available; otherwise inline context with the same system instruction.`}
9. **Output guardrails** scan the model reply and may soften advisory phrasing before returning to the client.

## What this prompt should cover
- Vera's role, tone, and Fides-specific boundaries (no personal financial advice, no guarantees, etc.).
- How to use portfolio context vs general fund knowledge.
- Markdown formatting expectations for investor-readable replies.
- When to defer to human support or official documents.

## What this prompt does **not** control
- Intent classification → **Intent Prompt** tab.
- Whether Google Search runs → Global Configuration + Intent result → **Google Search Prompt** tab (when search is on).
- Domain exclusions for search → Global Configuration (\`veraGoogleSearchExcludeDomains\`).

## Sandbox vs production
- Edits here auto-save to GCS for the preview session only.
- **Submit Proposal** sends your draft to engineering; production still uses the coded file until merged and deployed.

---
${searchStatusLine(options)}
`;
}

export function buildGlobalIntentGuide(options: VeraPromptGuideOptions): string {
  const searchOn = !!options.googleSearchEnabled;
  return `# Vera Investor Chat — Intent Prompt

## What you are editing
A **lightweight classifier prompt** for the investor's latest message. The model must output **exactly one word**: \`ADVICE\`, \`LOCATION\`, or \`ALLOWED\`.

**Coded production file:** \`investment-management-backend/src/services/vera-intent-instruction.md\`

## When it runs
- **In parallel** with the main Vera Gemini call on every global chat turn — it adds **no extra latency** to the investor experience.
- Same classifier family is used in production \`VeraGlobalChatService.chat()\` and in this admin sandbox preview.

## How the result is used
| Output | Effect |
|--------|--------|
| \`ADVICE\` | Main Vera call is **not** made (or preview shows guardrail refusal). Blocks personal financial advice requests. |
| \`LOCATION\` | Triggers Google Maps / verified location context fetch **in parallel** with the main call. Location data may be appended to the user message. |
| \`ALLOWED\` | Normal Vera response path. |

## Interaction with Google Search
- Google Search is **never** used when intent is \`ADVICE\`.
- When intent is \`ALLOWED\` or \`LOCATION\`${searchOn ? ' **and search is enabled** (global config or sandbox)' : ''}, the **Google Search Grounding Prompt** may be appended and the search tool attached to the main call.
${searchOn ? '- In this sandbox, search is currently **enabled** — tune classifier boundaries carefully so factual public questions (`ALLOWED` / `LOCATION`) are not misclassified as `ADVICE`.' : '- Search is currently **disabled** here — the Google Search prompt tab is hidden until you enable search globally or in this sandbox.'}

## Authoring tips
- Keep the prompt **short and precise** — it is a routing label, not a coaching essay.
- Use clear examples of \`ADVICE\` vs informational \`ALLOWED\` questions.
- \`LOCATION\` should capture suburb/property/map questions that need verified geography data.

## Sandbox vs production
- Preview chat uses your sandbox intent text when saved; production uses the coded file until a proposal is deployed.

---
${searchStatusLine(options)}
`;
}

export function buildGlobalGroundingGuide(options: VeraPromptGuideOptions): string {
  if (!options.googleSearchEnabled) {
    return `# Vera Investor Chat — Google Search Prompt

## Status
Google Search is **not enabled** in this sandbox. This prompt is **not applied** until you turn on:
- **Global Configuration → Enable Vera Google Search Grounding**, or
- **Google Search in this sandbox** (preview-only toggle).

When enabled, return to this tab for the full usage guide and to edit the grounding rules.

---
${searchStatusLine(options)}
`;
  }

  return `# Vera Investor Chat — Google Search Grounding Prompt

## What you are editing
Rules appended to the **System Prompt** when Gemini's **Google Search** tool is attached for investor global chat. This is **stricter** than the admin assist search prompt.

**Coded production file:** \`investment-management-backend/src/services/vera-global-chat-google-search-grounding-instruction.md\`

## When it runs
All of the following must be true:
1. Intent classifier returned \`ALLOWED\` or \`LOCATION\` (not \`ADVICE\`).
2. **Enable Vera Google Search Grounding** is on in Global Configuration **or** sandbox search is enabled for preview.
3. The main Vera call uses **inline** delivery (search and Vertex prompt cache are mutually exclusive).

## How it is applied
- \`resolveGlobalChatGoogleSearch()\` builds a config with \`excludeDomains\` (baseline social/UGC list + global config additions).
- \`applyGoogleSearchGrounding()\` appends **this prompt text** to the system instruction and registers the \`googleSearch\` tool on the Gemini chat config.
- The model may query the public web for factual gaps **after** using portfolio context and (if \`LOCATION\`) Maps data.

## Citation links in apps
- Search can run even when links are hidden.
- **Enable Vera Global Chat Search Links** (Global Configuration) controls whether \`googleSearchGrounding\` (sources + inline citation spans) is returned to web and native clients.
${options.googleSearchLinksEnabled ? '- Links are **enabled** — investors may see numbered citation chips on grounded replies.' : '- Links are **disabled** — grounded replies look like normal text in apps; search may still inform the answer.'}

## What this prompt should cover
- When search is allowed vs forbidden (e.g. no advice, prefer Fides/portfolio data first).
- Acceptable factual topics (demographics, council facts, public market stats) vs off-limits areas.
- Citation behaviour — investors see numbered markers linking to deduplicated sources when links are on.
- Tone: investor-facing, cautious, no fabricated citations.

## What this prompt does **not** control
- Turning search on/off → Global Configuration + Intent result.
- Domain blocklist → Global Configuration exclude domains (shared with admin assist).
- Main Vera personality → **System Prompt** tab.

## Sandbox vs production
- Sandbox can test search without changing global config.
- When global search is on, sandbox **cannot** disable search for preview (matches production).

---
${searchStatusLine(options)}
`;
}

export function buildGlobalFullPipelineGuide(options: VeraPromptGuideOptions): string {
  const searchOn = !!options.googleSearchEnabled;
  return `# Vera Investor Chat — Full Prompt Pipeline (AI Studio context)

Use this when proposing changes across multiple prompts. Three editable layers work together on every investor global chat turn.

## Request flow (production)
\`\`\`
Investor message
    │
    ├─► [Intent Prompt] ── parallel ──► ADVICE | LOCATION | ALLOWED
    │
    ├─► if ADVICE → block / refusal (no main Vera call)
    │
    ├─► Load portfolio AI context + FAQ + date
    │
    ├─► [System Prompt] → Gemini systemInstruction
    │       ${searchOn ? '+ [Google Search Prompt] appended if ALLOWED/LOCATION + search on' : '(no search append when search off)'}
    │       ${searchOn ? '+ googleSearch tool attached → inline mode (no Vertex cache)' : '+ Vertex cache when available'}
    │
    ├─► if LOCATION → Maps context → append to user message
    │
    ├─► Chat history + user message → Gemini
    │
    └─► Output guardrails → reply${options.googleSearchLinksEnabled && searchOn ? ' + optional citation metadata' : ''}
\`\`\`

## The three editable prompts

### 1. System Prompt
**File:** \`vera-system-instruction.md\`
Main Vera personality, guardrails, formatting, use of portfolio data.

### 2. Intent Prompt
**File:** \`vera-intent-instruction.md\`
One-word classifier; gates advice blocking, Maps fetch, and search eligibility.

### 3. Google Search Grounding Prompt${searchOn ? '' : ' (inactive while search off)'}
**File:** \`vera-global-chat-google-search-grounding-instruction.md\`
${
  searchOn
    ? 'Search rules appended to system instruction when intent ≠ ADVICE and search is enabled.'
    : 'Not used until Enable Vera Google Search Grounding (global) or sandbox search is on.'
}

## Configuration outside these prompts
- \`enableVeraGoogleSearchGrounding\` — master search toggle (investor chat)
- \`enableVeraGlobalChatSearchLinks\` — return citation metadata to clients (${options.googleSearchLinksEnabled ? 'on' : 'off'})
- \`veraGoogleSearchExcludeDomains\` — extra blocked domains for search

---
${searchStatusLine(options)}

## Section guides
Copy the per-tab **"Copy for AI Studio"** block when editing a single prompt in isolation.
`;
}
